import { RemoteCaller as RemoteCallerClass } from "@/handlers/remoteCaller";

import type {
  AlmTool,
  AlmToolCallOpts,
  HandlerContext,
  RpcBackend,
  UnknownRecord,
} from "./RpcBackend";

const APP = "piplanning";
const API_VERSION = 6;

type RemoteCallerCtor = new (
  context: unknown,
  app: string,
  apiVersion: number,
  submodule: string | null,
) => {
  call(method: string, ...params: unknown[]): Promise<unknown>;
  callThrowing(method: string, ...params: unknown[]): Promise<unknown>;
};

const RemoteCaller = RemoteCallerClass as unknown as RemoteCallerCtor;

export class WampRpcBackend implements RpcBackend {
  constructor(private readonly context: HandlerContext) {}

  private invoke<T>(
    submodule: string | null,
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    throwing = false,
  ): Promise<T> {
    const remote = new RemoteCaller(this.context, APP, API_VERSION, submodule);
    const params = kwargs === undefined ? [args ?? []] : [args ?? [], kwargs];
    return (
      throwing ? remote.callThrowing(method, ...params) : remote.call(method, ...params)
    ) as Promise<T>;
  }

  rootCall<T>(method: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.invoke(null, method, args, kwargs);
  }

  sessionCall<T>(
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    _sessionId?: string,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    return this.invoke("session", method, args, kwargs, opts?.throwing);
  }

  almCall<T>(method: string, args?: unknown[], kwargs?: UnknownRecord): Promise<T> {
    return this.invoke("alm", method, args, kwargs);
  }

  almToolCall<T>(
    tool: AlmTool,
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    const result = this.invoke<T>(`alm.${tool}`, method, args, kwargs, opts?.throwing);
    // WAMP-only shim: can_connect's reply shape varies on WAMP (an [ok, message]
    // tuple, a { success: false, error } envelope, or null/scalar); normalize it to
    // the { ok, error } the NATS stubs return natively. Lives here so it is deleted
    // together with WAMP.
    if (method === "can_connect") {
      return result.then(normalizeCanConnectResult) as Promise<T>;
    }
    return result;
  }

  almSyncCall<T>(
    tool: AlmTool,
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    _sessionId?: string,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    return this.invoke(`alm.${tool}`, method, args, kwargs, opts?.throwing);
  }

  onboardingCall<T>(
    method: string,
    args?: unknown[],
    kwargs?: UnknownRecord,
    opts?: AlmToolCallOpts,
  ): Promise<T> {
    return this.invoke("onboarding", method, args, kwargs, opts?.throwing);
  }

  canHandle(_submodule: string, _method: string): boolean {
    return true;
  }
}

// WAMP-only shim: normalize a `can_connect` reply to the `{ ok, error }` the NATS
// stubs return natively. WAMP can yield an `[ok, message]` tuple, a
// `{ success: false, error }` envelope (managed/cancelled failures), or a
// null/scalar. Delete this together with WAMP.
function normalizeCanConnectResult(result: unknown): { ok: boolean; error: string } {
  if (Array.isArray(result)) {
    return { ok: Boolean(result[0]), error: typeof result[1] === "string" ? result[1] : "" };
  }
  if (result && typeof result === "object") {
    const r = result as { success?: unknown; error?: unknown };
    return { ok: Boolean(r.success), error: typeof r.error === "string" ? r.error : "" };
  }
  return { ok: Boolean(result), error: "" };
}
